// Apollo Client link for Application Insights tracking
import { ApolloLink, FetchResult, Observable, Operation } from "@apollo/client";
import { getCorrelationHeaders, trackApiCall, trackApiResponse } from "../app-insights";

export class ApplicationInsightsLink extends ApolloLink {
  request(operation: Operation, forward: ApolloLink.ForwardFunction): Observable<FetchResult> {
    // Get proper W3C correlation headers for Application Insights
    const correlationHeaders = getCorrelationHeaders();

    // Add correlation headers for proper Application Map linking
    operation.setContext({
      headers: {
        ...operation.getContext().headers,
        ...correlationHeaders, // Includes Request-Id, X-Correlation-ID, and traceparent
        "X-GraphQL-Operation": operation.operationName || "unknown",
      },
    });

    // Track GraphQL operation start
    trackApiCall(`GraphQL:${operation.operationName}`, {
      operationType: operation.query.definitions[0]?.kind,
      operationName: operation.operationName,
      variables: operation.variables,
    });

    const startTime = performance.now();

    return new Observable((observer) => {
      const subscription = forward(operation).subscribe({
        next: (result: FetchResult) => {
          const duration = performance.now() - startTime;
          const hasErrors = result.errors && result.errors.length > 0;

          // Track GraphQL operation completion
          trackApiResponse(`GraphQL:${operation.operationName}`, !hasErrors, duration, {
            operationType: operation.query.definitions[0]?.kind,
            operationName: operation.operationName,
            hasData: !!result.data,
            errorCount: result.errors?.length || 0,
            errors: hasErrors ? result.errors?.map((e) => e.message) : undefined,
          });

          observer.next(result);
        },
        error: (error: Error) => {
          const duration = performance.now() - startTime;

          // Track error
          trackApiResponse(`GraphQL:${operation.operationName}`, false, duration, {
            operationType: operation.query.definitions[0]?.kind,
            operationName: operation.operationName,
            error: error.message || String(error),
          });

          observer.error(error);
        },
        complete: () => {
          observer.complete();
        },
      });

      return () => {
        subscription.unsubscribe();
      };
    });
  }
}
